/**
 * NotificationsPage.tsx
 *
 * Alerts page — view and manage user notifications.
 * Displays a paginated list of notifications with read/unread state,
 * mark-all-read, per-item delete, and unread-only filter.
 */
import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { usePatientHistoryStore } from '@/store/patientHistoryStore';
import { usePageContext } from '@/hooks/usePageContext';
import { Trash2, CheckCheck, Inbox, Mail } from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { cn, formatCount } from '@/lib/utils';
import { NOTIFICATIONS_KEY, useUnreadAlertCount } from '@/hooks/useUnreadAlertCount';
import {
  getNotifications,
  markRead,
  markAllRead,
  deleteNotification,
  type Notification,
} from '@/services/notifications.service';
import {
  NOTIFICATION_TYPE_GROUPS,
  notificationPatientName,
  notificationTitle,
  openNotification,
} from '@/lib/notification-display';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function relativeTime(dateStr: string): string {
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return '';
  const now = Date.now();
  const diffMs = now - date.getTime();
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHr = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHr / 24);

  if (diffSec < 60) return 'just now';
  if (diffMin < 60) return `${diffMin} minute${diffMin === 1 ? '' : 's'} ago`;
  if (diffHr < 24) return `${diffHr} hour${diffHr === 1 ? '' : 's'} ago`;
  if (diffDay < 30) return `${diffDay} day${diffDay === 1 ? '' : 's'} ago`;

  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function authorName(n: Notification): string | null {
  if (n.authorFirstName || n.authorLastName) {
    return [n.authorFirstName, n.authorLastName].filter(Boolean).join(' ');
  }
  return null;
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function NotificationsPage() {
  const navigate = useNavigate();
  const updateAIContext = usePageContext('Alerts');
  const visitPatient = usePatientHistoryStore((s) => s.visit);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  // The shared count the sidebar's Alerts badge and the Dashboard card read, so
  // the three can never show different numbers. Each write below refreshes it.
  const { data: unreadCount = 0 } = useUnreadAlertCount();
  const queryClient = useQueryClient();
  const refreshUnreadCount = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_KEY });
  }, [queryClient]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [typeFilterIndex, setTypeFilterIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const pageSize = 25;

  // ── Fetch data ──────────────────────────────────────────────────────────

  const fetchNotifications = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await getNotifications(page, pageSize, unreadOnly || undefined);
      setNotifications(result.data);
      // Both are optional on the meta envelope; the setters are not. A missing
      // page count means one page, a missing total means none.
      setTotalPages(result.meta.totalPages ?? 1);
      setTotalCount(result.meta.total ?? 0);
    } catch {
      setError('Failed to load notifications.');
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, unreadOnly]);

  useEffect(() => {
    updateAIContext({
      filters: unreadOnly ? 'Showing unread only' : 'Showing all notifications',
      dataSummary: `${totalCount} notification(s) total, ${unreadCount} unread`,
    });
  }, [updateAIContext, unreadOnly, totalCount, unreadCount]);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  // ── Actions ─────────────────────────────────────────────────────────────

  const handleMarkRead = useCallback(async (id: number) => {
    try {
      await markRead(id);
      setNotifications((prev) =>
        prev.map((n) => (n.notificationId === id ? { ...n, isRead: true } : n)),
      );
      refreshUnreadCount();
    } catch {
      // ignore
    }
  }, [refreshUnreadCount]);

  const handleMarkAllRead = useCallback(async () => {
    try {
      await markAllRead();
      setNotifications((prev) => prev.map((n) => ({ ...n, isRead: true })));
      refreshUnreadCount();
      if (unreadOnly) {
        fetchNotifications();
      }
    } catch {
      // ignore
    }
  }, [unreadOnly, fetchNotifications, refreshUnreadCount]);

  const handleDelete = useCallback(async (id: number) => {
    try {
      await deleteNotification(id);
      setNotifications((prev) => prev.filter((n) => n.notificationId !== id));
      setTotalCount((c) => Math.max(0, c - 1));
      refreshUnreadCount();
    } catch {
      // ignore
    }
  }, [refreshUnreadCount]);

  const handleToggleUnreadOnly = useCallback(() => {
    setUnreadOnly((prev) => !prev);
    setPage(1);
  }, []);

  // ── Derived data ───────────────────────────────────────────────────────

  const activeTypeGroup = NOTIFICATION_TYPE_GROUPS[typeFilterIndex];
  const filteredNotifications = activeTypeGroup.typeIds
    ? notifications.filter((n) => n.typeId !== null && activeTypeGroup.typeIds!.includes(n.typeId))
    : notifications;

  // ── Render ──────────────────────────────────────────────────────────────

  return (
    <div className="p-6 space-y-6">
      <PageHeader
        title="Alerts"
        actions={
          <div className="flex items-center gap-3">
            {unreadCount > 0 && (
              <Badge variant="info" size="md">
                {unreadCount} unread
              </Badge>
            )}
            <Button
              variant="secondary"
              size="sm"
              leftIcon={<CheckCheck className="h-4 w-4" />}
              onClick={handleMarkAllRead}
              disabled={unreadCount === 0}
            >
              Mark All Read
            </Button>
          </div>
        }
      />

      {/* Filter bar */}
      <div className="flex items-center gap-3">
        <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={unreadOnly}
            onChange={handleToggleUnreadOnly}
            className="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
          />
          Show unread only
        </label>
        <select
          value={typeFilterIndex}
          onChange={(e) => {
            setTypeFilterIndex(Number(e.target.value));
            setPage(1);
          }}
          className="rounded border border-slate-300 bg-white px-2 py-1 text-sm text-slate-600 focus:border-blue-500 focus:ring-blue-500"
        >
          {NOTIFICATION_TYPE_GROUPS.map((group, idx) => (
            <option key={idx} value={idx}>
              {group.label}
            </option>
          ))}
        </select>
      </div>

      {/* Notification list */}
      <div className="rounded-lg border border-slate-200 bg-white overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-sm text-slate-500">Loading notifications...</div>
        ) : error ? (
          <div className="p-8 text-center text-sm text-red-500">{error}</div>
        ) : filteredNotifications.length === 0 ? (
          <div className="p-12 text-center">
            <Inbox className="h-10 w-10 text-slate-300 mx-auto mb-3" />
            <p className="text-sm font-medium text-slate-600">No notifications</p>
            <p className="text-xs text-slate-400 mt-1">
              {unreadOnly
                ? 'You have no unread notifications.'
                : 'You have no notifications at this time.'}
            </p>
          </div>
        ) : (
          <ul className="divide-y divide-slate-100">
            {filteredNotifications.map((n) => {
              const patient = notificationPatientName(n);
              return (
                <li
                  key={n.notificationId}
                  className={cn(
                    'flex items-start gap-3 px-4 py-3 transition-colors',
                    !n.isRead && 'bg-blue-50/40',
                    n.isRead && 'hover:bg-slate-50',
                  )}
                >
                  {/* Unread indicator */}
                  <div className="mt-1.5 flex-shrink-0">
                    {!n.isRead ? (
                      <span className="block h-2.5 w-2.5 rounded-full bg-blue-500" />
                    ) : (
                      <span className="block h-2.5 w-2.5 rounded-full bg-transparent" />
                    )}
                  </div>

                  {/* Content — clickable to mark as read and dispatch.
                      Lead / payment / appointment / note / survey-response
                      notifications open the corresponding shared modal;
                      everything else routes to the related detail page. */}
                  <button
                    type="button"
                    className="flex-1 min-w-0 text-left"
                    onClick={() => {
                      void openNotification(n, {
                        navigate,
                        visitPatient,
                        markRead: handleMarkRead,
                      });
                    }}
                  >
                    <p className="text-xs font-medium text-primary mb-0.5">
                      {n.display?.title || notificationTitle(n)}
                    </p>
                    {(n.display?.body || n.message) && (
                      <p
                        className={cn(
                          'text-sm text-slate-700 leading-snug',
                          !n.isRead && 'font-semibold',
                        )}
                      >
                        {n.display?.body || n.message}
                      </p>
                    )}
                    <div className="mt-1 flex items-center gap-2 text-xs text-slate-400">
                      <span>{relativeTime(n.createDate)}</span>
                      {patient && (
                        <>
                          <span aria-hidden="true">&middot;</span>
                          <span className="text-slate-500">{patient}</span>
                        </>
                      )}
                      {authorName(n) && (
                        <>
                          <span aria-hidden="true">&middot;</span>
                          <span className="text-slate-500">From: {authorName(n)}</span>
                        </>
                      )}
                      {n.isEmailed && (
                        <>
                          <span aria-hidden="true">&middot;</span>
                          <Mail className="h-3 w-3 text-slate-400" aria-label="Emailed" />
                        </>
                      )}
                    </div>
                  </button>

                  {/* Delete button */}
                  <Button
                    variant="ghost"
                    size="xs"
                    iconOnly
                    onClick={() => handleDelete(n.notificationId)}
                    aria-label="Delete notification"
                    className="flex-shrink-0 mt-0.5 text-slate-400 hover:text-red-500"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between text-sm text-slate-600">
          <span>
            Page {formatCount(page)} of {formatCount(totalPages)} ({formatCount(totalCount)} total)
          </span>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="rounded border border-slate-300 px-3 py-1 hover:bg-slate-50 disabled:opacity-40"
            >
              Previous
            </button>
            <button
              type="button"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="rounded border border-slate-300 px-3 py-1 hover:bg-slate-50 disabled:opacity-40"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
